import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaBuildingColumns } from 'react-icons/fa6'
import styled, { useTheme } from 'styled-components'
import { LucaIcon } from '@components/LucaIcon'
import { IBusinessOverviewRow, IOverviewCountTotal } from '@query'
import dayjs from 'dayjs'
import { ColumnKey } from './columns'
import { formatCents, formatTimeAgo, warningDark } from './format'

const CellValue = styled.div<{ $color?: string }>`
  font-size: ${({ theme }) => theme.fontSize.px135}rem;
  font-weight: 700;
  color: ${({ theme, $color }) => $color || theme.colors.metalGray};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`

const CellSub = styled.div<{ $color?: string }>`
  font-size: ${({ theme }) => theme.fontSize.px11}rem;
  color: ${({ theme, $color }) => $color || theme.colors.iconGray};
  font-weight: 600;
  margin-top: 2px;
  white-space: nowrap;
`

const BigValue = styled.div<{ $color?: string }>`
  font-size: ${({ theme }) => theme.fontSize.px14}rem;
  font-weight: 700;
  color: ${({ theme, $color }) => $color || theme.colors.metalGray};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`

const UnitSuffix = styled.span`
  font-size: ${({ theme }) => theme.fontSize.px105}rem;
  color: ${({ theme }) => theme.colors.iconGray};
  font-weight: 600;
`

const BankList = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.metalGray};
  min-width: 0;
  overflow-wrap: anywhere;
`

export const Muted = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.borderStrong};
  font-weight: 500;
`

const CountTotalCell: React.FC<{
  data?: IOverviewCountTotal
  emptySub?: string
  valueColor?: string
  subColor?: string
  showTotalAsValue?: boolean
}> = ({ data, emptySub, valueColor, subColor, showTotalAsValue }) => {
  const theme = useTheme()
  const count = data?.count || 0

  if (showTotalAsValue) {
    return (
      <div style={{ minWidth: 0 }}>
        <CellValue $color={count > 0 ? valueColor : theme.colors.iconGray}>
          {count > 0 ? formatCents(data?.total_cents || 0) : '—'}
        </CellValue>
        <CellSub $color={count > 0 ? subColor : undefined}>{emptySub}</CellSub>
      </div>
    )
  }

  return (
    <div style={{ minWidth: 0 }}>
      <CellValue>{count > 0 ? String(count) : '—'}</CellValue>
      <CellSub>{count > 0 ? formatCents(data?.total_cents || 0) : ''}</CellSub>
    </div>
  )
}

export const MetricCell: React.FC<{ column: ColumnKey; row: IBusinessOverviewRow }> = ({
  column,
  row
}) => {
  const [t, i18n] = useTranslation()
  const theme = useTheme()

  switch (column) {
    case 'sales':
      return <CountTotalCell data={row.sales} />
    case 'purchase':
      return <CountTotalCell data={row.purchase} />
    case 'sales_unpaid': {
      // Null metrics mean the row's usership lacks the column permission —
      // never render them as reassuring zero-states ("all paid").
      if (row.sales_unpaid === null) return <Muted>—</Muted>
      const count = row.sales_unpaid?.count || 0
      return (
        <CountTotalCell
          data={row.sales_unpaid}
          showTotalAsValue
          emptySub={
            count > 0
              ? t('businessesPage.table.open', { count })
              : t('businessesPage.table.allPaid')
          }
        />
      )
    }
    case 'sales_overdue': {
      if (row.sales_overdue === null) return <Muted>—</Muted>
      const count = row.sales_overdue?.count || 0
      return (
        <CountTotalCell
          data={row.sales_overdue}
          showTotalAsValue
          valueColor={warningDark(theme)}
          subColor={count > 0 ? warningDark(theme) : undefined}
          emptySub={
            count > 0
              ? t('businessesPage.table.overdueCount', { count })
              : t('businessesPage.table.none')
          }
        />
      )
    }
    case 'purchase_overdue': {
      if (row.purchase_overdue === null) return <Muted>—</Muted>
      const count = row.purchase_overdue?.count || 0
      return (
        <CountTotalCell
          data={row.purchase_overdue}
          showTotalAsValue
          valueColor={warningDark(theme)}
          subColor={count > 0 ? warningDark(theme) : undefined}
          emptySub={
            count > 0
              ? t('businessesPage.table.overdueCount', { count })
              : t('businessesPage.table.none')
          }
        />
      )
    }
    case 'debt': {
      if (row.debt === null) return <Muted>—</Muted>
      const count = row.debt?.count || 0
      return (
        <CountTotalCell
          data={row.debt}
          showTotalAsValue
          valueColor={warningDark(theme)}
          subColor={count > 0 ? warningDark(theme) : undefined}
          emptySub={
            count > 0
              ? t('businessesPage.table.caseCount', { count })
              : t('businessesPage.table.none')
          }
        />
      )
    }
    case 'drafts':
      return (
        <BigValue $color={row.drafts ? undefined : theme.colors.iconGray}>
          {row.drafts ? String(row.drafts) : '—'}
        </BigValue>
      )
    case 'docs_month':
      if (row.docs_month === null) return <Muted>—</Muted>
      return (
        <BigValue>
          {String(row.docs_month || 0)}
          <UnitSuffix> {t('businessesPage.table.perMonth')}</UnitSuffix>
        </BigValue>
      )
    case 'docs_year':
      if (row.docs_year === null) return <Muted>—</Muted>
      return (
        <BigValue>
          {String(row.docs_year || 0)}
          <UnitSuffix> {t('businessesPage.table.perYear')}</UnitSuffix>
        </BigValue>
      )
    case 'bank_count': {
      const count = row.bank_count || 0
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: `${theme.spacing.sm}rem` }}>
          <FaBuildingColumns
            style={{
              fontSize: `${theme.fontSize.px125}rem`,
              color: count > 0 ? theme.colors.dashboardExtra2 : theme.colors.borderStrong
            }}
          />
          <BigValue $color={count > 0 ? undefined : theme.colors.iconGray}>
            {count > 0 ? String(count) : '—'}
          </BigValue>
        </div>
      )
    }
    case 'last_sync': {
      if (!row.last_sync_at) {
        // Integrations exist but never synced is an error state; no
        // integrations at all is just an empty cell.
        if (row.has_bank_integrations) {
          return (
            <div
              style={{
                fontSize: `${theme.fontSize.sm}rem`,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                color: theme.colors.error
              }}
            >
              {t('businessesPage.table.never')}
            </div>
          )
        }
        return <Muted>—</Muted>
      }
      const daysAgo = dayjs().diff(dayjs(row.last_sync_at), 'day')
      return (
        <div
          style={{
            fontSize: `${theme.fontSize.sm}rem`,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            color:
              daysAgo > 1
                ? theme.colors.error
                : daysAgo === 1
                ? theme.colors.iconGray
                : theme.colors.metalGray
          }}
        >
          {formatTimeAgo(row.last_sync_at, i18n.language)}
        </div>
      )
    }
    case 'customer_bank': {
      if (row.customer_banks === null) return <Muted>—</Muted>
      const banks = row.customer_banks || []
      if (banks.length === 0) return <Muted>—</Muted>
      // Every connected bank, in the same plain typography as the other
      // cell values.
      return <BankList>{banks.join(', ')}</BankList>
    }
    case 'luca':
      if (row.luca_documents === null) return <Muted>—</Muted>
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: `${theme.spacing.xs}rem` }}>
          <LucaIcon
            size={`${theme.fontSize.px14}rem`}
            style={{ color: theme.colors.primary, flexShrink: 0 }}
          />
          <BigValue $color={theme.colors.menuBg}>{String(row.luca_documents || 0)}</BigValue>
        </div>
      )
    default:
      return null
  }
}
